import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Skeleton, Tooltip } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MapIcon from '@mui/icons-material/Map';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import floorplanService from '../../../Pages/Siteplans/services/floorplanService';

/**
 * Component for generating a floorplan thumbnail around a specific pin location
 */
const FloorplanThumbnail = ({
    floorplanId,
    xPct,
    yPct,
    floorplanName,
    projectId,
    deficiencyId,
}) => {
    const [thumbnailUrl, setThumbnailUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [pinPosition, setPinPosition] = useState(null);
    const canvasRef = useRef(null);
    const theme = useTheme();
    const navigate = useNavigate();

    const handleThumbnailClick = async () => {
        if (projectId && floorplanId) {
            try {
                // Get the floorplan data to pass to the viewplan page
                const floorplanData = await floorplanService.getFloorplanById(floorplanId);

                // Navigate with the selectedPlan state and highlight info that the Viewplan component expects
                navigate(`/projects/${projectId}/plans/${floorplanId}`, {
                    state: {
                        selectedPlan: {
                            id: floorplanData.id,
                            fileName: floorplanData.fileName,
                            imageUrl: floorplanData.imageUrl,
                            projectId: floorplanData.projectId,
                            dateCreated: floorplanData.dateCreated,
                            dateUpdated: floorplanData.dateUpdated,
                        },
                        highlightDeficiencyId: deficiencyId, // Pass the deficiency ID for highlighting
                        fromSidebar: true, // Flag to indicate navigation came from sidebar
                    },
                });
            } catch (error) {
                // If floorplan fetch fails, navigate without state (fallback)
                navigate(`/projects/${projectId}/plans/${floorplanId}`);
            }
        }
    };

    useEffect(() => {
        if (!floorplanId || xPct === undefined || yPct === undefined) {
            setLoading(false);
            setError(true);
            return;
        }

        const generateThumbnail = async () => {
            try {
                setLoading(true);
                setError(false);

                // Get the floorplan image URL - use the same pattern as deficiency images
                let imageUrl;

                // Try to get from the current page first (if we're on a viewplan page)
                const currentImageElement = document.querySelector(
                    'img[alt*="Plan"], img[src*="floorplan"], img[src*="siteplan"]'
                );
                if (currentImageElement && currentImageElement.src) {
                    imageUrl = currentImageElement.src;
                } else {
                    // Get floorplan data to get the proper image URL
                    try {
                        const floorplanData = await floorplanService.getFloorplanById(floorplanId);
                        imageUrl = floorplanData.imageUrl;

                        if (!imageUrl) {
                            throw new Error('No image URL in floorplan data');
                        }
                    } catch (error) {
                        // If floorplan doesn't exist (404) or other error, try to find image on current page
                        const currentImageElement = document.querySelector(
                            'img[alt*="Plan"], img[src*="floorplan"], img[src*="siteplan"]'
                        );
                        if (currentImageElement && currentImageElement.src) {
                            imageUrl = currentImageElement.src;
                        } else {
                            throw new Error('Floorplan not found and no image available on page');
                        }
                    }
                }

                const img = new Image();
                img.crossOrigin = 'use-credentials';

                img.onload = () => {
                    const canvas = canvasRef.current;
                    if (!canvas) {
                        return;
                    }

                    const ctx = canvas.getContext('2d');
                    const canvasSize = 150;
                    canvas.width = canvasSize;
                    canvas.height = canvasSize;

                    // Calculate the area around the pin
                    const zoomFactor = 0.25; // Show 25% of the image around the pin
                    const cropWidth = img.width * zoomFactor;
                    const cropHeight = img.height * zoomFactor;

                    // Calculate crop position centered on the pin
                    // xPct and yPct are already in decimal form (0.225 = 22.5%)
                    const pinX = xPct * img.width;
                    const pinY = yPct * img.height;

                    let cropX = pinX - cropWidth / 2;
                    let cropY = pinY - cropHeight / 2;

                    // Ensure crop area doesn't go outside image bounds
                    cropX = Math.max(0, Math.min(cropX, img.width - cropWidth));
                    cropY = Math.max(0, Math.min(cropY, img.height - cropHeight));

                    // Draw the cropped image
                    ctx.drawImage(
                        img,
                        cropX,
                        cropY,
                        cropWidth,
                        cropHeight,
                        0,
                        0,
                        canvasSize,
                        canvasSize
                    );

                    // Calculate pin position on the canvas (for overlay positioning)
                    const canvasPinX = ((pinX - cropX) / cropWidth) * canvasSize;
                    const canvasPinY = ((pinY - cropY) / cropHeight) * canvasSize;

                    // Store pin position for overlay
                    setPinPosition({ x: canvasPinX, y: canvasPinY });

                    // Convert to data URL
                    setThumbnailUrl(canvas.toDataURL());
                    setLoading(false);
                };

                img.onerror = () => {
                    setError(true);
                    setLoading(false);
                };

                img.src = imageUrl;
            } catch (err) {
                setError(true);
                setLoading(false);
            }
        };

        generateThumbnail();
    }, [floorplanId, xPct, yPct]);

    // Cleanup on component unmount
    useEffect(() => {
        return () => {
            if (thumbnailUrl && thumbnailUrl.startsWith('blob:')) {
                URL.revokeObjectURL(thumbnailUrl);
            }
        };
    }, [thumbnailUrl]);

    if (loading) {
        return (
            <Tooltip title="Click to view on floor plan" arrow>
                <Box
                    onClick={handleThumbnailClick}
                    sx={{
                        position: 'relative',
                        cursor: 'pointer',
                        '&:hover': {
                            transform: 'scale(1.02)',
                            transition: 'transform 0.2s ease-in-out',
                        },
                    }}
                >
                    <Skeleton
                        variant="rounded"
                        width={150}
                        height={150}
                        sx={{ borderRadius: 1.5, flexShrink: 0 }}
                    />
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                </Box>
            </Tooltip>
        );
    }

    if (error || !thumbnailUrl) {
        return (
            <Tooltip title="Click to view on floor plan" arrow>
                <Box
                    onClick={handleThumbnailClick}
                    sx={{
                        width: 150,
                        height: 150,
                        border: '1px solid',
                        borderColor: theme.palette.grey[200],
                        borderRadius: 1.5,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: theme.palette.grey[50],
                        gap: 0.5,
                        flexShrink: 0,
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: theme.palette.grey[100],
                            transition: 'background-color 0.2s ease-in-out',
                        },
                    }}
                >
                    <MapIcon sx={{ color: theme.palette.grey[400], fontSize: 18 }} />
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        align="center"
                        sx={{ fontSize: '0.65rem' }}
                    >
                        Preview unavailable
                    </Typography>
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                </Box>
            </Tooltip>
        );
    }

    return (
        <Tooltip title="Click to view on floor plan" arrow>
            <Box
                onClick={handleThumbnailClick}
                sx={{
                    position: 'relative',
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: theme.palette.grey[300],
                    flexShrink: 0,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    cursor: 'pointer',
                    '&:hover': {
                        transform: 'scale(1.02)',
                        transition: 'transform 0.2s ease-in-out',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    },
                }}
            >
                <img
                    src={thumbnailUrl}
                    alt={`${floorplanName} - Pin Location`}
                    style={{
                        width: 150,
                        height: 150,
                        objectFit: 'cover',
                        display: 'block',
                    }}
                />
                {/* Overlay the actual LocationOnIcon */}
                {pinPosition && (
                    <LocationOnIcon
                        sx={{
                            position: 'absolute',
                            left: pinPosition.x,
                            top: pinPosition.y,
                            transform: 'translate(-50%, -100%)', // Center horizontally, point at location
                            color: theme.palette.error.main,
                            fontSize: 20,
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                            pointerEvents: 'none',
                        }}
                    />
                )}
                <canvas ref={canvasRef} style={{ display: 'none' }} />
            </Box>
        </Tooltip>
    );
};

/**
 * Component for displaying and editing location information
 *
 * @param {Object} props - Component props
 * @param {Object} props.deficiency - The deficiency object containing location data
 * @param {boolean} props.isEditing - Whether edit mode is active
 * @param {Function} props.onChange - Callback when location is changed
 * @returns {React.ReactElement} Location section component
 */
const LocationSection = ({ deficiency, isEditing, onChange }) => {
    const theme = useTheme();
    const { projectId } = useParams();
    const location = deficiency?.location || '';

    // Extract new floorplan and ROI information
    const floorplanId = deficiency?.floorplanId;
    const floorplanName = deficiency?.floorplanName;
    const floorplanGroupName = deficiency?.floorplanGroupName;
    const roiName = deficiency?.roiName;
    const xPct = deficiency?.xPct;
    const yPct = deficiency?.yPct;
    const deficiencyId = deficiency?.deficiencyId;

    const handleLocationChange = (event) => {
        if (onChange) {
            onChange({
                ...deficiency,
                location: event.target.value,
            });
        }
    };

    return (
        <>
            <Typography
                variant="caption"
                sx={{
                    fontWeight: 600,
                    color: '#666666',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 2,
                    display: 'block',
                }}
            >
                Location Details
            </Typography>

            {/* Main Location Info */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                {/* Floorplan Thumbnail */}
                <FloorplanThumbnail
                    floorplanId={floorplanId}
                    xPct={xPct}
                    yPct={yPct}
                    floorplanName={floorplanName}
                    projectId={projectId}
                    deficiencyId={deficiencyId}
                />

                {/* Location Info */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Building (only shown for multi-building projects) */}
                    {floorplanGroupName && (
                        <Box>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    fontWeight: 600,
                                    fontSize: '0.7rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    mb: 0.5,
                                    display: 'block',
                                }}
                            >
                                Building
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <MapIcon sx={{ color: theme.palette.primary.main, fontSize: 16 }} />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    {floorplanGroupName}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Floor Plan */}
                    <Box>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                mb: 0.5,
                                display: 'block',
                            }}
                        >
                            Floor Plan
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MapIcon sx={{ color: theme.palette.primary.main, fontSize: 16 }} />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: theme.palette.text.primary,
                                }}
                            >
                                {floorplanName || 'Unknown Floor Plan'}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Room/ROI */}
                    <Box>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                mb: 0.5,
                                display: 'block',
                            }}
                        >
                            Room/Area
                        </Typography>
                        <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                        >
                            {roiName ? (
                                <>
                                    <LocationOnIcon
                                        sx={{ color: theme.palette.success.main, fontSize: 16 }}
                                    />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: '0.875rem',
                                            fontWeight: 500,
                                            color: theme.palette.text.primary,
                                        }}
                                    >
                                        {roiName}
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <LocationOnIcon
                                        sx={{ color: theme.palette.grey[400], fontSize: 16 }}
                                    />
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontSize: '0.875rem' }}
                                    >
                                        No room assigned
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Additional Location Text Field */}
            {isEditing ? (
                <TextField
                    fullWidth
                    id="location"
                    label="Additional Location Notes"
                    value={location}
                    onChange={handleLocationChange}
                    placeholder="Enter specific location details (optional)"
                    multiline
                    rows={3}
                    size="small"
                    sx={{
                        mt: 1,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                        },
                    }}
                />
            ) : (
                location && (
                    <Box sx={{ mt: 2 }}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                mb: 0.5,
                                display: 'block',
                            }}
                        >
                            Additional Notes
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: '0.875rem',
                                lineHeight: 1.4,
                                color: theme.palette.text.primary,
                            }}
                        >
                            {location}
                        </Typography>
                    </Box>
                )
            )}
        </>
    );
};

export default LocationSection;
